import {
  Button,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
} from "@colorglo/ui/components";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useId, useState } from "react";
import { api } from "../api.ts";
import { useT } from "../lib/locale.ts";

export interface InviteSearch {
  readonly token?: string;
}

export const Route = createFileRoute("/inbjudan")({
  validateSearch: (search: Record<string, unknown>): InviteSearch =>
    typeof search.token === "string" ? { token: search.token } : {},
  component: AcceptInvitation,
});

function AcceptInvitation() {
  const t = useT();
  const { token } = Route.useSearch();

  const nameId = useId();
  const emailId = useId();
  const passwordId = useId();
  const hintId = useId();
  const errorId = useId();

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");

  const preview = useQuery({
    queryKey: ["invitation-preview", token],
    enabled: token !== undefined,
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.v1.invitations.preview.get({
        query: { token: token as string },
      });
      if (error !== null) throw (error as { value?: unknown }).value ?? error;
      return data;
    },
  });

  const isOwner = preview.data?.role === "franchise_owner";
  const hasAccount = preview.data?.has_account ?? false;

  const accept = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.v1.invitations.accept.post({
        token: token as string,
        ...(isOwner ? {} : { name }),
        ...(hasAccount ? {} : { password }),
      });
      if (error !== null) throw (error as { value?: unknown }).value ?? error;
      return data;
    },
  });

  if (token === undefined) {
    return (
      <div className="mx-auto max-w-md p-6">
        <p role="alert" className="text-body">
          {t("invite.missing")}
        </p>
      </div>
    );
  }

  if (accept.isSuccess) {
    return (
      <div className="mx-auto flex max-w-md flex-col gap-4 p-6">
        <h1 className="text-h1">{t("invite.done")}</h1>
        <Link to="/" className="text-body text-primary underline underline-offset-4">
          {t("nav.calendar")}
        </Link>
      </div>
    );
  }

  if (preview.isPending) return null;

  if (preview.isError || preview.data === undefined) {
    return (
      <div className="mx-auto max-w-md p-6">
        <p role="alert" className="text-body">
          {t("invite.invalid")}
        </p>
      </div>
    );
  }

  return (
    <form
      className="mx-auto max-w-md p-6"
      onSubmit={(event) => {
        event.preventDefault();
        accept.mutate();
      }}
    >
      <FieldGroup>
        <div className="flex flex-col gap-1">
          <h1 className="text-h1">
            {isOwner
              ? t("invite.owner_title", { name: preview.data.franchise_name })
              : t("invite.title")}
          </h1>
          <p className="text-body-sm text-muted-foreground">
            {hasAccount
              ? t("invite.activate_body", { name: preview.data.franchise_name })
              : isOwner
                ? t("invite.owner_body")
                : t("invite.body")}
          </p>
        </div>

        {isOwner && (
          <Field>
            <FieldLabel htmlFor={emailId}>{t("invite.email")}</FieldLabel>
            <Input
              id={emailId}
              type="email"
              autoComplete="username"
              readOnly
              value={preview.data.email}
            />
          </Field>
        )}

        {!isOwner && (
          <Field>
            <FieldLabel htmlFor={nameId}>{t("invite.name")}</FieldLabel>
            <Input
              id={nameId}
              type="text"
              autoComplete="name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
        )}

        {hasAccount && accept.isError && (
          <p role="alert" className="text-body-sm text-destructive">
            {t("error.validation_failed")}
          </p>
        )}

        {!hasAccount && (
          <Field>
            <FieldLabel htmlFor={passwordId}>{t("invite.password")}</FieldLabel>
            <Input
              id={passwordId}
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              aria-describedby={accept.isError ? `${hintId} ${errorId}` : hintId}
              aria-invalid={accept.isError || undefined}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <FieldDescription id={hintId}>{t("invite.password_hint")}</FieldDescription>
            {accept.isError && <FieldError id={errorId}>{t("error.validation_failed")}</FieldError>}
          </Field>
        )}

        <Field>
          <Button type="submit" disabled={accept.isPending}>
            {hasAccount ? t("invite.activate") : t("invite.submit")}
          </Button>
        </Field>
      </FieldGroup>
    </form>
  );
}
